import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Award, CheckCircle2, ClipboardList, Building2, CalendarCheck } from "lucide-react";
import { doddServices, isDoddActive } from "../mock";
import useSeo from "../hooks/useSeo";

const Dodd = () => {
  useSeo({
    title: "Ohio DODD Services | Caregivers Alliance Homecare — HPC, Respite, Transportation, Shared Living",
    description: "Ohio DODD certified provider: Homemaker Personal Care, HPC Transportation, Non-Medical Transportation, Community & Residential Respite, Shared Living and Participant Directed HPC. Now accepting new patients and referrals: (614) 526-1548.",
    keywords: "Ohio DODD provider, HPC provider Ohio, DODD transportation provider, community respite Ohio, shared living provider Ohio, participant directed HPC Ohio",
    path: "/dodd",
  });
  const active = isDoddActive();

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div data-testid="dodd-effective-banner" className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <Award className="h-4 w-4" />
            {active ? "DODD Certified Provider — Now Accepting New Patients" : "DODD Certified — Now Accepting New Patients"}
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl">Ohio DODD Services</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            {active
              ? "Caregivers Alliance Homecare provides DODD-certified services in partnership with County Boards of Developmental Disabilities, SSAs and families across Ohio. Now accepting new patients."
              : "Now accepting new patients and referrals across Ohio. Submit referrals now so services start on time."}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/refer"><Button data-testid="dodd-refer-btn" className="rounded-full bg-white text-[#0E5A72] hover:bg-white/90 h-12 px-7 font-semibold">REFER AN INDIVIDUAL</Button></Link>
            <Link to="/contact"><Button className="rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 px-7 font-semibold">CHECK AVAILABILITY</Button></Link>
            <Link to="/providers"><Button className="rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/30 h-12 px-7 font-semibold">COUNTY BOARD / SSA CONTACT</Button></Link>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-display font-bold text-3xl text-[#0E2A33] text-center">Certified Services</h2>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {doddServices.map((s) => (
              <div key={s} className="flex items-start gap-3 bg-[#F7FBFC] rounded-2xl border border-slate-100 p-5">
                <CheckCircle2 className="h-5 w-5 text-[#2E9E6A] shrink-0 mt-0.5" />
                <span className="font-medium text-[#0E2A33]">{s}</span>
              </div>
            ))}
          </div>
          {!active && (
            <p className="text-center text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-3xl mx-auto mt-8">
              Now accepting new patients and referrals — submitting your referral now ensures care is in place on time.
            </p>
          )}
        </div>
      </section>

      <section className="section bg-[#F7FBFC]">
        <div className="mx-auto max-w-7xl px-4 grid md:grid-cols-3 gap-6">
          {[
            { icon: ClipboardList, title: "1. Send a referral", text: "SSAs, County Boards and families submit a referral online or call (614) 526-1548. No detailed medical information is needed at this stage." },
            { icon: Building2, title: "2. We coordinate", text: "We review the individual's service plan and authorization with the referral team and confirm staffing feasibility for the county and schedule." },
            { icon: CalendarCheck, title: "3. Planned start", text: "Once eligibility, authorization and staffing align, we schedule the service start." },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7">
                <div className="brand-gradient h-11 w-11 rounded-xl grid place-items-center text-white mb-4"><Icon className="h-5 w-5" /></div>
                <h3 className="font-display font-bold text-lg text-[#0E2A33]">{c.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.text}</p>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default Dodd;
